import { TimingState } from '@rhg/components/media/types';
import { logger } from '@rhg/libs/logger';
import { useMediaPreferences } from '@rhg/providers/media-preferences';
import { isIOS } from '@rhg/utils/browser';
import { defaultLanguage } from '@rhg/utils/intl';
import {
    AudioEvent,
    NORMAL_PLAYBACK_RATE,
    readStoredPlaybackRate,
    VideoEvent,
    writeStoredPlaybackRate,
} from '@rhg/utils/media';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useCurrentLanguage } from '../use-current-language';
import { MediaControls, MediaControlsHookProps } from './types';

export function useMediaControls({
    mediaEl,
    type,
    isInitiallyMuted,
    fullscreenContainerRef,
    shouldInterruptPlay,
    persistPlaybackRate,
    shouldSkipTimingUpdate,
}: MediaControlsHookProps): MediaControls {
    const currentLanguage = useCurrentLanguage();
    const [isMuted, setIsMuted] = useState(!!isInitiallyMuted);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const { isShowingSubtitles, setIsShowingSubtitles } = useMediaPreferences();
    const [currentCue, setCurrentCue] = useState<VTTCue | undefined>(undefined);
    const [timingState, setTimingState] = useState<TimingState>({
        isPaused: true,
        duration: 0,
        remainingTime: 0,
        currentTime: 0,
        currentPercent: 0,
        bufferedPercent: 0,
    });
    const [isPausedByUserInteraction, setIsPausedByUserInteraction] =
        useState<boolean>();
    // Low-frequency playback state. Unlike `timingState` (rebuilt on every
    // `timeupdate`, ~4Hz), these change only when playback actually
    // starts/stops or the loaded track changes, so consumers that only care
    // about play/pause/track identity don't re-render during playback.
    // EVE-12668.
    const [isPaused, setIsPaused] = useState(true);
    const [activeSrc, setActiveSrc] = useState<string | undefined>(undefined);
    const [playbackRate, setPlaybackRateState] = useState(() =>
        persistPlaybackRate ? readStoredPlaybackRate() : NORMAL_PLAYBACK_RATE
    );

    const setPlaybackRate = useCallback((rate: number) => {
        setPlaybackRateState(rate);
        writeStoredPlaybackRate(rate);
    }, []);

    const play = useCallback(() => {
        if (mediaEl) {
            mediaEl.play().catch((error) => {
                logger.error('[media-controls] play failed', {}, error);
            });
        }
    }, [mediaEl]);

    const pause = useCallback(() => {
        if (mediaEl) {
            mediaEl.pause();
        }
    }, [mediaEl]);

    /**
     * There are cases where the audio/video should be interrupted by
     * an external event (e.g. a dialog open). This is controlled by the
     * `shouldInterruptPlay` param.
     */
    const hasRemainingTime = timingState.remainingTime !== 0;
    const hasStartedPlayback = timingState.currentTime > 0;
    useEffect(() => {
        /**
         * If this param is not passed at all, then ignore this effect.
         */
        if (typeof shouldInterruptPlay !== 'boolean') {
            return;
        }

        /**
         * If this param is set to `true`, then interrupt the audio/video
         * by calling the `pause` callback.
         */
        if (shouldInterruptPlay) {
            pause();
            return;
        }

        /**
         * If `shouldInterruptPlay` is set to `false` and the audio/video has
         * not ended, then we want to trigger the `play` callback in only two cases:
         *
         * 1. Either the audio/video has not yet started
         * 2. Or the user has not paused the audio/video on demand
         */
        if (
            !shouldInterruptPlay &&
            hasRemainingTime &&
            (!hasStartedPlayback || !isPausedByUserInteraction)
        ) {
            play();
            return;
        }
    }, [
        isPausedByUserInteraction,
        pause,
        play,
        shouldInterruptPlay,
        hasStartedPlayback,
        hasRemainingTime,
    ]);

    /**
     * Callback that handles click or keyboard events triggered by user interaction.
     * This is passed as a click/key down handler to the play/pause buttons.
     */
    const togglePlay = useCallback(() => {
        if (!mediaEl) return;

        const isPaused = mediaEl.paused;
        if (isPaused) {
            setIsPausedByUserInteraction(false);
            mediaEl.play().catch((error) => {
                logger.error('[media-controls] play failed', {}, error);
            });
        } else {
            setIsPausedByUserInteraction(true);
            mediaEl.pause();
        }
    }, [mediaEl]);

    const toggleSubtitles = useCallback(
        (forcedState?: boolean) => {
            setIsShowingSubtitles(
                typeof forcedState === 'undefined'
                    ? !isShowingSubtitles
                    : forcedState
            );
        },
        [isShowingSubtitles, setIsShowingSubtitles]
    );

    const toggleFullscreen = useCallback(() => {
        if (type === 'video') {
            if (fullscreenContainerRef && !isIOS()) {
                if (document.fullscreenElement) {
                    document.exitFullscreen().catch((err: unknown) => {
                        logger.error(
                            '[media-controls] exitFullscreen failed',
                            {},
                            err as Error
                        );
                    });
                    setIsFullscreen(false);
                } else if (fullscreenContainerRef?.current?.requestFullscreen) {
                    fullscreenContainerRef.current
                        ?.requestFullscreen()
                        .catch((err: unknown) => {
                            logger.error(
                                '[media-controls] requestFullscreen failed',
                                {},
                                err as Error
                            );
                        });
                    setIsFullscreen(true);
                }
            } else {
                if (document.fullscreenElement === mediaEl) {
                    document.exitFullscreen().catch((err: unknown) => {
                        logger.error(
                            '[media-controls] exitFullscreen failed',
                            {},
                            err as Error
                        );
                    });
                    setIsFullscreen(false);
                } else {
                    mediaEl?.requestFullscreen().catch((err: unknown) => {
                        logger.error(
                            '[media-controls] requestFullscreen failed',
                            {},
                            err as Error
                        );
                    });
                    setIsFullscreen(true);
                }
            }
        }
    }, [fullscreenContainerRef, type, mediaEl]);

    // React state is the source of truth for mute. The effect below applies it
    // to the media element, so toggling never reads from the (possibly stale or
    // freshly-remounted) element. `forcedState` sets an explicit value; omit it
    // to flip the current state.
    const toggleMuted = useCallback((forcedState?: boolean) => {
        setIsMuted((prev) =>
            typeof forcedState === 'boolean' ? forcedState : !prev
        );
    }, []);

    const setTiming = useCallback(
        (newTime?: number) => {
            if (!mediaEl) return;
            // An undefined newTime means this call came from a media event
            // (timeupdate/play/pause/…), not a programmatic seek. Drop those
            // event-driven ticks while asked to skip — e.g. during a
            // Safari/Firefox route transition — so the ~4Hz React churn doesn't
            // block the transition commit. Seeks (newTime provided) still
            // apply. AND, not OR: OR would drop every event tick unconditionally
            // and the scrubber would never advance. EVE-13083.
            const isEventTick = newTime === undefined;
            if (isEventTick && shouldSkipTimingUpdate?.()) {
                return;
            }
            const isPaused = mediaEl.paused;
            const duration = mediaEl?.duration ?? 0;
            const mediaElTime =
                !isNaN(mediaEl?.currentTime) && mediaEl?.currentTime > 0
                    ? mediaEl.currentTime
                    : 0;
            const currentTime = newTime !== undefined ? newTime : mediaElTime;

            if (newTime !== undefined) {
                mediaEl.currentTime = currentTime ?? 0;
            }

            if (currentTime !== undefined && !isNaN(currentTime)) {
                const _duration =
                    !isNaN(duration) && duration > 0 ? duration : 0;
                setTimingState({
                    isPaused,
                    duration: _duration,
                    remainingTime: _duration - currentTime,
                    currentTime,
                    currentPercent: _duration ? currentTime / _duration : 0,
                    bufferedPercent: 100, // TODO actually calculate and set this eventually
                });
            }
        },
        [mediaEl, setTimingState, shouldSkipTimingUpdate]
    );

    const setTime = useCallback(
        (newTime: number) => {
            if (
                !isNaN(newTime) &&
                newTime >= 0 &&
                newTime < Number.MAX_SAFE_INTEGER
            ) {
                setTiming(newTime);
            }
        },
        [setTiming]
    );

    const showCue = useCallback((cue?: VTTCue) => {
        setCurrentCue(cue);
    }, []);

    useEffect(() => {
        if (!mediaEl) return;
        if (type === 'video') {
            Object.values(VideoEvent).forEach((eventType) =>
                mediaEl?.addEventListener(eventType, () => {
                    setTiming();
                })
            );
        } else {
            Object.values(AudioEvent).forEach((eventType) => {
                mediaEl.addEventListener(eventType, () => {
                    setTiming();
                });
            });
        }
    }, [mediaEl, setTiming, type]);

    // Reset timingState the instant mediaEl tears down (e.g. between playlist
    // tracks). Without this, the scrubber keeps rendering the outgoing
    // track's last position — often near the end — until the next track's
    // element fires its first timeupdate, and briefly animates from there
    // back to 0. useLayoutEffect (not useEffect) so it lands before paint.
    // EVE-13651.
    useLayoutEffect(() => {
        if (mediaEl) return;
        setTimingState({
            isPaused: true,
            duration: 0,
            remainingTime: 0,
            currentTime: 0,
            currentPercent: 0,
            bufferedPercent: 0,
        });
    }, [mediaEl]);

    // Track the low-frequency playback state (isPaused / activeSrc) from
    // dedicated media events. These fire only on genuine state changes — not
    // on `timeupdate` — so the promoted state is DOM-truth AND triggers a
    // render exactly when play/pause/track flips, letting UI unify on
    // `isPaused` (previously the UI needed `timingState.isPaused` to re-render
    // while analytics read the live `mediaEl.paused`). EVE-12668.
    useEffect(() => {
        if (!mediaEl) {
            setIsPaused(true);
            setActiveSrc(undefined);
            return;
        }
        const syncPlayState = () => setIsPaused(mediaEl.paused);
        const syncLoadedTrack = () => {
            setActiveSrc(mediaEl.currentSrc || undefined);
            setIsPaused(mediaEl.paused);
        };
        // Seed from the element's current state — it may already be
        // playing/loaded before these listeners attach.
        syncLoadedTrack();
        mediaEl.addEventListener('play', syncPlayState);
        mediaEl.addEventListener('pause', syncPlayState);
        mediaEl.addEventListener('ended', syncPlayState);
        mediaEl.addEventListener('loadedmetadata', syncLoadedTrack);
        mediaEl.addEventListener('emptied', syncLoadedTrack);
        return () => {
            mediaEl.removeEventListener('play', syncPlayState);
            mediaEl.removeEventListener('pause', syncPlayState);
            mediaEl.removeEventListener('ended', syncPlayState);
            mediaEl.removeEventListener('loadedmetadata', syncLoadedTrack);
            mediaEl.removeEventListener('emptied', syncLoadedTrack);
        };
    }, [mediaEl]);

    // Apply the selected playback rate to the media element. Re-applies when the
    // element changes (e.g. a track change resets the browser default to 1).
    useEffect(() => {
        if (!mediaEl) return;
        mediaEl.playbackRate = playbackRate;
    }, [mediaEl, playbackRate]);

    // Apply the mute state to the media element. Re-applies when the element
    // changes (e.g. a track/session change creates a fresh element that defaults
    // to unmuted) so the audio output always matches the UI mute state.
    useEffect(() => {
        if (!mediaEl) return;
        mediaEl.muted = isMuted;
    }, [mediaEl, isMuted]);

    // Subtitles https://stackoverflow.com/questions/23475335/subtitles-for-audio-with-track-how-to-display-the-subtitles
    const setTextTracksListener = useCallback(() => {
        if (!mediaEl) return;

        const tracks = Array.from(mediaEl.textTracks);
        const hasMatchingLanguageTrack = tracks.some(
            (t) =>
                (t.kind === 'subtitles' || t.kind === 'captions') &&
                t.language === (currentLanguage as string)
        );
        tracks.forEach((track) => {
            if (track.kind === 'subtitles' || track.kind === 'captions') {
                if (type === 'video') {
                    if (
                        !hasMatchingLanguageTrack &&
                        track.language === (defaultLanguage as string)
                    ) {
                        track.mode = 'hidden';
                    } else if (track.language === (currentLanguage as string)) {
                        track.mode = 'hidden';
                    } else {
                        track.mode = 'disabled';
                    }
                    track.addEventListener('cuechange', () => {
                        const activeCues = track.activeCues;
                        showCue(
                            activeCues && activeCues?.length > 0
                                ? (activeCues[0] as VTTCue)
                                : undefined
                        );
                    });
                } else {
                    track.mode = 'hidden';
                    track.addEventListener('cuechange', (e) => {
                        const activeCues = (e.target as TextTrack).activeCues;
                        showCue((activeCues?.[0] as VTTCue) ?? undefined);
                    });
                }
            }
        });
    }, [mediaEl, currentLanguage, showCue, type]);

    useEffect(() => {
        if (!mediaEl) return;

        setCurrentCue(undefined);
        mediaEl.addEventListener('loadedmetadata', setTextTracksListener);

        return () => {
            if (!mediaEl) return;
            mediaEl.textTracks.removeEventListener(
                'addtrack',
                setTextTracksListener
            );
            mediaEl.textTracks.removeEventListener(
                'removetrack',
                setTextTracksListener
            );
        };
    }, [mediaEl, setTextTracksListener]);

    // Pause and play based on space
    useEffect(() => {
        if (!mediaEl) return;

        const handleKey = (e: KeyboardEvent) => {
            if (e.code === 'Space') {
                e.preventDefault();
                togglePlay();
            }
        };
        document.addEventListener('keyup', handleKey);
        return () => {
            document.removeEventListener('keyup', handleKey);
        };
    }, [mediaEl, togglePlay]);

    return {
        isFullscreen,
        isMuted,
        isShowingSubtitles,
        isPaused,
        activeSrc,
        hasSubtitles: (mediaEl?.textTracks.length ?? 0) > 0,
        currentCue,
        timingState,
        togglePlay,
        toggleSubtitles,
        toggleFullscreen,
        toggleMuted,
        setTime,
        playbackRate,
        setPlaybackRate,
    };
}
